import { DEFAULT_RESEARCH_COUNT } from '@dupe/common/constants'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

type ResearchModeState = {
  enabled: boolean
  archetype: string
  count: number
}

interface IResearchModeContext {
  researchMode: ResearchModeState
  setResearchModeEnabled: (enabled: boolean) => void
  setResearchArchetype: (archetype: string) => void
  setResearchCount: (count: number) => void
}

const DEFAULT_ARCHETYPE = 'research-analyst'

/** localStorage key used to persist the `enabled` toggle across tabs so the
 *  homepage ↔ /chats/v2 round trip (which uses `window.open`, spawning a new
 *  provider instance) doesn't drop the user's choice. Only the `enabled` bit
 *  persists — archetype/count are chosen per-task. */
const RESEARCH_MODE_ENABLED_STORAGE_KEY = 'dupe:research-mode-enabled'

const defaultContext: IResearchModeContext = {
  researchMode: {
    enabled: false,
    archetype: DEFAULT_ARCHETYPE,
    count: DEFAULT_RESEARCH_COUNT,
  },
  setResearchModeEnabled: () => {},
  setResearchArchetype: () => {},
  setResearchCount: () => {},
}

const ResearchModeContext = createContext<IResearchModeContext>(defaultContext)

export const useResearchModeContext = () => {
  const context = useContext(ResearchModeContext)

  if (context === undefined) {
    throw new Error(
      'useResearchModeContext() must be used within a ResearchModeProvider'
    )
  }

  return context
}

interface Props extends React.PropsWithChildren {}

export const ResearchModeProvider: React.FC<Props> = ({ children }) => {
  const [researchMode, setResearchMode] = useState<ResearchModeState>({
    enabled: false,
    archetype: DEFAULT_ARCHETYPE,
    count: DEFAULT_RESEARCH_COUNT,
  })

  // Hydrate from localStorage on mount (client-only — avoids SSR hydration
  // mismatch by deferring the read to an effect instead of the initializer).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const stored = window.localStorage.getItem(
      RESEARCH_MODE_ENABLED_STORAGE_KEY
    )
    if (stored === '1') {
      setResearchMode((prev) => ({ ...prev, enabled: true }))
    }
  }, [])

  const setResearchModeEnabled = useCallback((enabled: boolean) => {
    setResearchMode((prev) => ({ ...prev, enabled }))
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        RESEARCH_MODE_ENABLED_STORAGE_KEY,
        enabled ? '1' : '0'
      )
    }
  }, [])

  const setResearchArchetype = useCallback((archetype: string) => {
    setResearchMode((prev) => ({ ...prev, archetype }))
  }, [])

  const setResearchCount = useCallback((count: number) => {
    setResearchMode((prev) => ({ ...prev, count }))
  }, [])

  return (
    <ResearchModeContext.Provider
      value={{
        researchMode,
        setResearchModeEnabled,
        setResearchArchetype,
        setResearchCount,
      }}
    >
      {children}
    </ResearchModeContext.Provider>
  )
}
